// ═══════════════════════════════════════════════════════════════════════
// BELLANOR AP — Camada de dados Supabase
//
// Funções para buscar/salvar dados no Supabase, com mapeamento
// entre o formato do banco (snake_case, UUID) e o formato do
// frontend (camelCase, numérico).
//
// Regra: se o Supabase falhar, retorna null → App.js usa localStorage.
// ═══════════════════════════════════════════════════════════════════════

import { supabase, isSupabaseReady } from "./supabase";

// ─── Helpers de mapeamento ──────────────────────────────────────────

// Converte snake_case do DB → camelCase do frontend
function snakeToCamel(obj) {
  if (!obj || typeof obj !== "object") return obj;
  if (Array.isArray(obj)) return obj.map(snakeToCamel);
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    const camel = k.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
    out[camel] = v;
  }
  return out;
}

// Converte camelCase do frontend → snake_case pro DB
function camelToSnake(obj) {
  if (!obj || typeof obj !== "object") return obj;
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    const snake = k.replace(/[A-Z]/g, c => "_" + c.toLowerCase());
    out[snake] = v;
  }
  return out;
}

// ─── Cache de mapeamento UUID ↔ índice numérico ─────────────────────
// O frontend usa IDs numéricos (1, 2, 3...) nos componentes.
// O Supabase usa UUIDs. Mantemos um mapa bidirecional.
const uuidToNum = {};
const numToUuid = {};
let nextId = 1;

function assignNumericId(uuid) {
  if (uuidToNum[uuid]) return uuidToNum[uuid];
  const num = nextId++;
  uuidToNum[uuid] = num;
  numToUuid[num] = uuid;
  return num;
}

// Mapa separado pra times
const timeUuidToId = {};
const timeIdToUuid = {};

function assignTimeId(uuid, idStr) {
  // Se o time já tem um id string (ex: "oncas-fortal"), usa ele
  // Senão, cria um baseado no uuid
  const tid = idStr || `time-${uuid.slice(0, 8)}`;
  timeUuidToId[uuid] = tid;
  timeIdToUuid[tid] = uuid;
  return tid;
}

// ═══════════════════════════════════════════════════════════════════════
// CORRETORES
// ═══════════════════════════════════════════════════════════════════════

// Agrega vendas por corretor em janelas semana/mes/ano (frontend, evita campos stale)
async function fetchVendasAgregadas() {
  if (!isSupabaseReady) return {};
  try {
    const agora = new Date();
    const inicioAno = new Date(agora.getFullYear(), 0, 1);
    const inicioMes = new Date(agora.getFullYear(), agora.getMonth(), 1);
    // Semana: domingo 00:00 → sábado 23:59 (ciclo comercial BR)
    const diaSem = agora.getDay(); // 0=dom, 6=sab
    const inicioSemana = new Date(agora.getFullYear(), agora.getMonth(), agora.getDate() - diaSem);
    // Só vendas REAIS contam pra ranking/pontos: confirmada ou paga.
    // Pendente (aguardando confirmação), cancelada, recusada, estornada NÃO contam.
    const { data, error } = await supabase
      .from("vendas")
      .select("corretor_id, data_pedido, valor, valor_liquido, status")
      .gte("data_pedido", inicioAno.toISOString())
      .in("status", ["confirmada", "paga"]);
    if (error) throw error;
    const agg = {};
    for (const v of data || []) {
      if (!v.corretor_id) continue;
      const dt = new Date(v.data_pedido);
      const valor = parseFloat(v.valor_liquido ?? v.valor ?? 0) || 0;
      if (!agg[v.corretor_id]) agg[v.corretor_id] = { ano: 0, mes: 0, semana: 0, countAno: 0, countMes: 0, countSemana: 0 };
      const a = agg[v.corretor_id];
      // Contagem AO VIVO de vendas confirmadas/pagas (1 venda = 1 ponto no período)
      a.countAno += 1; a.ano += valor;
      if (dt >= inicioMes) { a.countMes += 1; a.mes += valor; }
      if (dt >= inicioSemana) { a.countSemana += 1; a.semana += valor; }
    }
    return agg;
  } catch (err) {
    console.warn("[Bellanor] fetchVendasAgregadas falhou:", err.message);
    return {};
  }
}

export async function fetchCorretores() {
  if (!isSupabaseReady) return null;
  try {
    const [corResp, agg] = await Promise.all([
      supabase
        .from("corretores")
        .select("*, times(id, nome, cidade, uf, emoji)")
        .or("tipo.eq.corretor,tipo.eq.proprietario,tipo.eq.admin,tipo.eq.admin_master,tipo.is.null")
        .order("nome"),
      fetchVendasAgregadas(),
    ]);
    const { data, error } = corResp;

    if (error) throw error;
    if (!data?.length) return null;

    // Mapeia pro formato do frontend
    return data.map((c) => {
      const ag = agg[c.id] || { ano: 0, mes: 0, semana: 0, countAno: 0, countMes: 0, countSemana: 0 };
      const numId = assignNumericId(c.id);
      const timeId = c.time_id
        ? assignTimeId(c.time_id, c.times?.nome?.toLowerCase().replace(/\s+/g, "-").slice(0, 20))
        : null;

      return {
        // IDs
        id: numId,
        _uuid: c.id, // preserva UUID pra writes
        timeId,

        // Dados básicos
        nome: c.nome || "",
        foto: c.foto_url || null,
        email: c.email || "",
        telefone: c.telefone || "",
        cidade: c.cidade || "",
        uf: c.uf || "",
        codigoIndicacao: c.codigo_indicacao || "",
        tagUnica: c.tag_unica || "",
        sexo: c.sexo || "M",
        awardVitalicio: c.award_atual || null,
        ativo: c.ativo !== false,
        banido: c.banido === true,
        tipo: c.tipo || "corretor",
        pontosBonus: c.pontos_bonus || 0,

        // Dados de rede
        indicadoPor: c.indicado_por ? assignNumericId(c.indicado_por) : null,
        cadastrados: c.total_cadastrados || 0,
        ativados: c.total_ativados || 0,
        ativadosInativos90d: 0, // computado depois

        // Pontuação AO VIVO das vendas reais (confirmadas/pagas) — 1 venda = 1 ponto no período.
        // Não usa mais as colunas cacheadas (ficavam desatualizadas).
        pontosAno: ag.countAno,
        pontosMes: ag.countMes,
        pontosSemana: ag.countSemana,
        ativacoesSemana: c.ativacoes_semana || 0,

        // Vendas — contagem + volume AO VIVO por semana/mes/ano.
        // vendasDiretas = vendas confirmadas/pagas no ano. Trocar corretor/cancelar/excluir zera sozinho.
        vendasDiretas: ag.countAno,
        vendasValor: ag.ano,
        vendasValorAno: ag.ano,
        vendasValorMes: ag.mes,
        vendasValorSemana: ag.semana,

        // Anti-monopólio (ranking)
        semanasComo1Time: 0,
        semanasComo1Regional: 0,
        semanasComo1Nacional: 0,
      };
    });
  } catch (err) {
    console.error("[Bellanor] Erro ao buscar corretores:", err.message);
    return null;
  }
}

// ═══════════════════════════════════════════════════════════════════════
// TIMES
// ═══════════════════════════════════════════════════════════════════════

export async function fetchTimes() {
  if (!isSupabaseReady) return null;
  try {
    const { data, error } = await supabase
      .from("times")
      .select("*")
      .eq("ativo", true)
      .order("nome");

    if (error) throw error;
    if (!data?.length) return null;

    return data.map((t) => {
      const tid = assignTimeId(t.id, t.slug || t.nome?.toLowerCase().replace(/\s+/g, "-").slice(0, 20));
      return {
        id: tid,
        _uuid: t.id,
        nome: t.nome || "",
        cidade: t.cidade || "",
        uf: t.uf || "",
        ativo: t.ativo !== false,
        emoji: t.emoji || "🏆",
      };
    });
  } catch (err) {
    console.error("[Bellanor] Erro ao buscar times:", err.message);
    return null;
  }
}

// ═══════════════════════════════════════════════════════════════════════
// CONFIGURAÇÕES (metas, aceleradores, prêmios, metas individuais)
// ═══════════════════════════════════════════════════════════════════════

export async function fetchConfig(chave) {
  if (!isSupabaseReady) return null;
  try {
    const { data, error } = await supabase
      .from("configuracoes")
      .select("valor")
      .eq("chave", chave)
      .maybeSingle();

    if (error) throw error;
    return data?.valor || null;
  } catch (err) {
    console.error(`[Bellanor] Erro ao buscar config '${chave}':`, err.message);
    return null;
  }
}

export async function saveConfig(chave, valor) {
  if (!isSupabaseReady) return false;
  try {
    const { error } = await supabase
      .from("configuracoes")
      .upsert(
        { chave, valor, atualizado_em: new Date().toISOString() },
        { onConflict: "chave" }
      );

    if (error) throw error;
    return true;
  } catch (err) {
    console.error(`[Bellanor] Erro ao salvar config '${chave}':`, err.message);
    return false;
  }
}

// ═══════════════════════════════════════════════════════════════════════
// USUÁRIOS / LOGIN
// ═══════════════════════════════════════════════════════════════════════
//
// MVP: autentica contra tabela 'usuarios' no Supabase.
// Futuro: migrar pra supabase.auth com magic link ou email/senha.
// ═══════════════════════════════════════════════════════════════════════

export async function loginUser(identificador, password) {
  if (!isSupabaseReady) return null;
  try {
    const raw = identificador.trim().toLowerCase().replace(/^@/, "");
    const isEmail = raw.includes("@");
    // 1) Tenta pelo username direto na tabela usuarios
    let { data, error } = await supabase
      .from("usuarios")
      .select("*")
      .eq(isEmail ? "email" : "username", raw)
      .eq("password", password)
      .maybeSingle();

    // 2) Fallback: se veio email, tenta achar corretor por email → usuarios.corretor_id
    if (!data && !error && isEmail) {
      const { data: cor } = await supabase
        .from("corretores")
        .select("id")
        .ilike("email", raw)
        .maybeSingle();
      if (cor?.id) {
        const r = await supabase
          .from("usuarios")
          .select("*")
          .eq("corretor_id", cor.id)
          .eq("password", password)
          .maybeSingle();
        data = r.data;
        error = r.error;
      }
    }

    // 3) Fallback: tenta resolver via tag_unica do corretor → corretor_id em usuarios
    if (!data && !error && !isEmail) {
      const { data: cor } = await supabase
        .from("corretores")
        .select("id")
        .ilike("tag_unica", raw)
        .maybeSingle();
      if (cor?.id) {
        const r = await supabase
          .from("usuarios")
          .select("*")
          .eq("corretor_id", cor.id)
          .eq("password", password)
          .maybeSingle();
        data = r.data;
        error = r.error;
      }
    }

    if (error) throw error;
    if (!data) return null;

    // Modules: garante defaults por role mesmo se DB estiver desatualizado
    let modules = Array.isArray(data.modules) ? data.modules.slice() : [];
    const isAdminRole = data.role === "admin" || data.role === "admin_master" || data.role === "proprietario" || data.role === "comercial";
    if (isAdminRole) {
      // admin/proprietario: full access
      const ALL = ["dashboard","vendas","propostas","ranking","corretores","financeiro","times","regional","nacional","clientes","rede","plantao","meusettings","config"];
      ALL.forEach(m => { if (!modules.includes(m)) modules.push(m); });
    } else if (data.role === "corretor") {
      const DEF = ["dashboard","ranking","financeiro","times","regional","nacional","rede","plantao","meusettings"];
      DEF.forEach(m => { if (!modules.includes(m)) modules.push(m); });
    }

    return {
      id: data.id,
      username: data.username,
      email: data.email || null,
      password: data.password,
      role: data.role,
      nome: data.nome,
      corretorId: data.corretor_id ? assignNumericId(data.corretor_id) : null,
      _corretorUuid: data.corretor_id || null,
      modules,
    };
  } catch (err) {
    console.error("[Bellanor] Erro no login Supabase:", err.message);
    return null; // fallback pro login local
  }
}

// ═══════════════════════════════════════════════════════════════════════
// SALVAR CORRETOR (update parcial)
// ═══════════════════════════════════════════════════════════════════════

export async function updateCorretor(numId, changes) {
  if (!isSupabaseReady) return false;
  const uuid = numToUuid[numId];
  if (!uuid) return false;

  try {
    // Converte campos do frontend pro formato do DB
    const dbChanges = {};
    if (changes.nome !== undefined) dbChanges.nome = changes.nome;
    if (changes.email !== undefined) dbChanges.email = changes.email;
    if (changes.telefone !== undefined) dbChanges.telefone = changes.telefone;
    if (changes.cidade !== undefined) dbChanges.cidade = changes.cidade;
    if (changes.uf !== undefined) dbChanges.uf = changes.uf;
    if (changes.ativo !== undefined) dbChanges.ativo = changes.ativo;
    if (changes.timeId !== undefined) dbChanges.time_id = timeIdToUuid[changes.timeId] || null;

    if (Object.keys(dbChanges).length === 0) return true;

    const { error } = await supabase
      .from("corretores")
      .update(dbChanges)
      .eq("id", uuid);

    if (error) throw error;
    return true;
  } catch (err) {
    console.error("[Bellanor] Erro ao atualizar corretor:", err.message);
    return false;
  }
}

// ═══════════════════════════════════════════════════════════════════════
// SALVAR TIME
// ═══════════════════════════════════════════════════════════════════════

export async function updateTime(timeId, changes) {
  if (!isSupabaseReady) return false;
  const uuid = timeIdToUuid[timeId];
  if (!uuid) return false;

  try {
    const dbChanges = {};
    if (changes.nome !== undefined) dbChanges.nome = changes.nome;
    if (changes.cidade !== undefined) dbChanges.cidade = changes.cidade;
    if (changes.uf !== undefined) dbChanges.uf = changes.uf;
    if (changes.emoji !== undefined) dbChanges.emoji = changes.emoji;
    if (changes.ativo !== undefined) dbChanges.ativo = changes.ativo;

    if (Object.keys(dbChanges).length === 0) return true;

    const { error } = await supabase
      .from("times")
      .update(dbChanges)
      .eq("id", uuid);

    if (error) throw error;
    return true;
  } catch (err) {
    console.error("[Bellanor] Erro ao atualizar time:", err.message);
    return false;
  }
}

// ═══════════════════════════════════════════════════════════════════════
// VENDAS (pra webhook e dashboard futuro)
// ═══════════════════════════════════════════════════════════════════════

export async function fetchVendas(filtros = {}) {
  if (!isSupabaseReady) return null;
  try {
    let query = supabase
      .from("vendas")
      .select("*, corretores(nome)")
      .order("data_pedido", { ascending: false });

    if (filtros.status) query = query.eq("status", filtros.status);
    if (filtros.corretorUuid) query = query.eq("corretor_id", filtros.corretorUuid);
    if (filtros.limit) query = query.limit(filtros.limit);

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  } catch (err) {
    console.error("[Bellanor] Erro ao buscar vendas:", err.message);
    return null;
  }
}

// ═══════════════════════════════════════════════════════════════════════
// HEALTH CHECK — verifica se o Supabase tá acessível
// ═══════════════════════════════════════════════════════════════════════

export async function checkConnection() {
  if (!isSupabaseReady) return { ok: false, reason: "Supabase não configurado" };
  try {
    const { error } = await supabase.from("times").select("id").limit(1);
    if (error) throw error;
    return { ok: true };
  } catch (err) {
    return { ok: false, reason: err.message };
  }
}
